import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { DataSourceDevice } from '@core/models/installation.model';
import { ActivityService } from '@core/services/activity.service';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { FedDevice } from '../../fed-devices';
import {
  ActivityRead,
  ActivityRow,
  activityIcon,
  activityRow,
  isInstallationActivity,
  messageWithoutIdentifiers,
} from '../../installation-activity';
import { InstallationActivityEmptyComponent } from '../installation-activity-empty/installation-activity-empty.component';

const SHOWN = 5;

export interface RecentActivityRow extends ActivityRow {
  key: string;
  icon: string;
  // The device a device's row comes from and what its message says, as far as the row has them.
  detail: string;
  initiatedOn: string;
  initiatedBy: string;
  // Only an action that did not succeed shows its status, so a failure stands out.
  showStatus: boolean;
}

@Component({
  selector: 'xemex-installation-recent-activity',
  imports: [
    InstallationActivityEmptyComponent,
    MatIconModule,
    SkeletonBlockComponent,
    TagComponent,
    TranslateModule,
  ],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-recent-activity.component.html',
  styleUrl: './installation-recent-activity.component.scss',
})
export class InstallationRecentActivityComponent {
  readonly installationOid = input.required<string>();
  // The devices that feed the installation, which the empty state points to.
  readonly devices = input.required<FedDevice[]>();
  // The devices at the installation's location, which name a device's rows by their type.
  readonly locationDevices = input<DataSourceDevice[]>([]);

  private readonly activityService = inject(ActivityService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);

  private readonly read = rxResource({
    params: () => this.installationOid(),
    stream: ({ params: installationOid }) =>
      this.activityService
        .getActivities({ installationOid, limit: SHOWN, offset: 0 })
        .pipe(catchError(() => of<ActivityRead>('failed'))),
  });

  readonly loading = computed(() => this.read.isLoading());
  readonly failed = computed(() => this.read.value() === 'failed');

  readonly rows = computed<RecentActivityRow[]>(() => {
    const read = this.read.value();
    if (!read || read === 'failed') return [];
    return read.activities.slice(0, SHOWN).map(activity => {
      const message = activity.message ? messageWithoutIdentifiers(activity.message) : null;
      const row = activityRow(
        this.translate,
        { ...activity, message: message ?? undefined },
        this.locationDevices(),
      );
      const source = isInstallationActivity(activity) ? null : row.source;
      return {
        ...row,
        key: `${activity.scope}-${activity.device_id}-${activity.correlation_id}`,
        icon: activityIcon(activity.action),
        detail: [source, row.message].filter(Boolean).join(' · '),
        initiatedOn: this.datePipe.transform(activity.initiated_on, 'd MMM yyyy, HH:mm') ?? '',
        initiatedBy: activity.initiated_by,
        showStatus: activity.status !== 'Success',
      };
    });
  });
}
